import { Button, Card } from '@/components/UI';
import { formatInt } from './regimeProfilePresentation';
import type { RegimeVetoCandidateCardModel } from './regimeVetoPresentation';

interface RegimeVetoCandidateCardProps {
    model: RegimeVetoCandidateCardModel;
    /** Non-null disables the action and names why. */
    disabledReason: string | null;
    isCreating: boolean;
    onCreateGatedVariant: () => void;
}

/**
 * Layer 3 — one veto candidate gate. Always exclusion-shaped; the single action is "Create gated
 * variant" (a sibling strategy plus a forked, seeded entry) — there is no "Apply to strategy" anywhere,
 * per backend §5.6/§5.7.
 */
export function RegimeVetoCandidateCard({ model, disabledReason, isCreating, onCreateGatedVariant }: RegimeVetoCandidateCardProps) {
    return (
        <Card className="bg-zinc-900 border border-zinc-800">
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">Candidate gate</div>

            <div className="flex flex-wrap items-center gap-2">
                <span className="rounded border border-amber-800/60 bg-amber-950/40 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-300">
                    Veto
                </span>
                <span className="font-mono text-sm text-zinc-100">{model.conditionDisplay}</span>
            </div>

            <div className="mt-4">
                <div className="flex items-baseline justify-between gap-2 text-sm">
                    <span className="text-zinc-400">Retains</span>
                    <span className={model.retention.meetsMinimum ? 'font-semibold text-green-400' : 'font-semibold text-amber-400'}>
                        {model.retainedShareDisplay} of trades
                    </span>
                </div>
                <div
                    aria-label={`Retained trade share ${model.retainedShareDisplay}; minimum ${model.minRetainedShareDisplay}`}
                    aria-valuemax={100}
                    aria-valuemin={0}
                    aria-valuenow={Number(model.retention.retainedPercent.toFixed(1))}
                    className="relative mt-1.5 h-3 overflow-hidden rounded bg-zinc-800"
                    role="meter"
                >
                    <div
                        className={`h-full rounded-l ${model.retention.meetsMinimum ? 'bg-green-600/70' : 'bg-amber-600/70'}`}
                        style={{ width: `${model.retention.retainedPercent}%` }}
                    />
                    <div
                        aria-hidden
                        className="absolute inset-y-0 border-l-2 border-dashed border-zinc-400/80"
                        style={{ left: `${model.retention.minMarkerPercent}%` }}
                    />
                </div>
                <div className="mt-1 text-xs text-zinc-500">
                    minimum {model.minRetainedShareDisplay}
                    {!model.retention.meetsMinimum && <span className="text-amber-400"> — below the retention minimum</span>}
                </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
                <span>
                    <span className="text-zinc-400">Mean profit </span>
                    <span className="text-zinc-200">{model.baselineMeanDisplay}</span>
                    <span aria-hidden className="text-zinc-500">
                        {' '}
                        →{' '}
                    </span>
                    <span className="sr-only"> to </span>
                    <span className="font-semibold text-zinc-100">{model.retainedMeanDisplay}</span>
                </span>
                <span>
                    <span className="text-zinc-400">Excluded share </span>
                    <span className="text-zinc-200">{model.excludedShareDisplay}</span>
                </span>
                <span>
                    <span className="text-zinc-400">Retained trades </span>
                    <span className="text-zinc-200">{formatInt(model.candidate.retainedTradeCount)}</span>
                </span>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-zinc-800 pt-3">
                <Button
                    disabled={disabledReason !== null || isCreating}
                    size="sm"
                    title={disabledReason ?? undefined}
                    onClick={onCreateGatedVariant}
                >
                    {isCreating ? 'Creating…' : 'Create gated variant'}
                </Button>
                <span className="text-xs text-zinc-500">Backtest this inactive family variant against its saved parent.</span>
            </div>

            <div className="mt-2 text-xs text-zinc-500">
                Create a child experiment only when this variant needs downstream optimization.
            </div>

            {disabledReason !== null && <div className="mt-1 text-xs text-amber-400">{disabledReason}</div>}
        </Card>
    );
}
